import { Alert } from 'antd';
import moment from 'moment-timezone';
import React from 'react';
import { AlertProps } from 'antd/lib/alert';

export interface INotification extends AlertProps {
    notificationId: string;
    occurredAt: string;
    lastUpdated: string;
    timezone: string;
    additionalInfo?: Map<string, string | undefined>;
    acknowledged: boolean;
    isHandled: boolean;
    isDismissable: boolean;
}

export interface IFireNotification {
    (id: string): void;
}

export type NotificationComponent = React.FC<{
    notification: INotification;
    fireNotificationDismissed?: IFireNotification;
    MessageComponent?: MessageComponent;
    DescriptionComponent?: DescriptionComponent;
    t: TranslateFunction;
}>;
export type DescriptionComponent = React.FC<{ notification: INotification; translatedDescription: React.ReactNode; t: TranslateFunction }>;

type MessageComponent = React.FC<{ translatedMessage: React.ReactNode; notification: INotification; t: TranslateFunction }>;

const BasicNotifications = ({
    notifications = new Map(),
    fireNotificationDismissed,
    notificationsShouldBeHandled = true,
    ComponentToRenderWith = BasicNotification,
    t,
}: React.PropsWithChildren<{
    notifications?: Map<string, INotification>;
    fireNotificationDismissed?: IFireNotification;
    notificationsShouldBeHandled?: boolean;
    ComponentToRenderWith?: NotificationComponent;
    t: TranslateFunction;
}>) => (
    <>
        {Array.from(notifications).reduce((acc, [id, notification]) => {
            const { acknowledged, isHandled } = notification;

            return !acknowledged && isHandled === notificationsShouldBeHandled
                ? [...acc, <ComponentToRenderWith key={id} notification={notification} fireNotificationDismissed={fireNotificationDismissed} t={t} />]
                : acc;
        }, [])}
    </>
);

const BasicNotification: NotificationComponent = ({ notification, fireNotificationDismissed, t, MessageComponent, DescriptionComponent }) => {
    const { notificationId, type, occurredAt, isDismissable } = notification;

    const translatedMessage = getNotificationMessage(t, notification);
    const translatedDescription = getNotificationDescription(t, notification);

    const message = MessageComponent ? <MessageComponent translatedMessage={translatedMessage} notification={notification} t={t} /> : translatedMessage;
    const description = DescriptionComponent ? (
        <DescriptionComponent translatedDescription={translatedDescription} notification={notification} t={t} />
    ) : (
        translatedDescription
    );

    return (
        <Alert
            key={`${notificationId}_${occurredAt}`}
            type={type}
            message={message}
            description={description}
            afterClose={() => fireNotificationDismissed?.(notificationId)}
            showIcon
            closable={isDismissable}
            style={{
                position: 'absolute',
                width: '100%',
                zIndex: 999,
            }}
        />
    );
};

interface DescriptionFunction<T = Record<string, unknown>> {
    (t: TranslateFunction<T>, notification: INotification): ReturnType<TranslateFunction<T>>;
}

interface TranslateFunction<T = Record<string, unknown>> {
    (t: string, options: T): React.ReactNode;
}

const getNotificationMessage: DescriptionFunction = (t, { notificationId, occurredAt, lastUpdated, timezone, additionalInfo = new Map() }) =>
    t(notificationId, {
        context: 'SUMMARY',
        occurredAt: moment.tz(occurredAt, timezone),
        lastUpdated: lastUpdated,
        additionalInfo: additionalInfo.size,
    });

const getNotificationDescription: DescriptionFunction = (t, { notificationId, occurredAt, lastUpdated, timezone, additionalInfo }) =>
    t(notificationId, {
        context: 'DESCRIPTION',
        occurredAt: moment.tz(occurredAt, timezone),
        lastUpdated: moment.tz(lastUpdated, timezone),
        additionalInfo: additionalInfo,
        returnObjects: true,
    });

export { BasicNotifications, BasicNotification, getNotificationMessage, getNotificationDescription };
